import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Calculator, CurrencyGbp, TrendUp, Trophy, Users, Gear,
  CaretDown, CaretUp
} from "@phosphor-icons/react";

export default function CommissionPage() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [salary, setSalary] = useState("30000");
  const [feePercentage, setFeePercentage] = useState("15");
  const [numPlacements, setNumPlacements] = useState("1");
  const [roleType, setRoleType] = useState("recruitment_partner");
  const [calcResult, setCalcResult] = useState(null);
  const role = user?.role || "opportunity_finder";

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [configRes, earningsRes] = await Promise.all([
        api.get("/commission/config"),
        api.get("/commission/my-earnings"),
      ]);
      setConfig(configRes.data);
      setEarnings(earningsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCalculate = async () => {
    if (!salary || parseFloat(salary) <= 0) { toast.error("Enter a valid salary"); return; }
    try {
      const { data } = await api.post("/commission/calculate", {
        placement_salary: parseFloat(salary),
        fee_percentage: parseFloat(feePercentage || "15"),
        num_placements: parseInt(numPlacements || "1"),
        role_type: roleType,
      });
      setCalcResult(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Calculation failed"); }
  };

  const fmt = (v) => `\u00a3${(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" /></div>;

  const rpCfg = config?.recruitment_partner || {};
  const srpCfg = config?.senior_recruitment_partner || {};
  const tlCfg = config?.team_leader || {};

  return (
    <div data-testid="commissions-page" className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">My Commissions & Earnings</h1>
        <p className="text-sm text-[#6B7280] mt-1">Track your placement earnings, bonuses, and commission structure</p>
      </div>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#10B98114] flex items-center justify-center">
              <CurrencyGbp size={20} className="text-[#10B981]" weight="duotone" />
            </div>
            <span className="text-xs text-[#6B7280] uppercase font-medium">Personal Commission</span>
          </div>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="personal-commission">{fmt(earnings?.total_personal_commission)}</p>
        </div>

        {role === "team_leader" && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#3B82F614] flex items-center justify-center">
                <Users size={20} className="text-[#3B82F6]" weight="duotone" />
              </div>
              <span className="text-xs text-[#6B7280] uppercase font-medium">Team Override</span>
            </div>
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="override-commission">{fmt(earnings?.total_override_commission)}</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#C5A05914] flex items-center justify-center">
              <TrendUp size={20} className="text-[#C5A059]" weight="duotone" />
            </div>
            <span className="text-xs text-[#6B7280] uppercase font-medium">Total Earnings</span>
          </div>
          <p className="text-2xl font-bold text-[#C5A059]" data-testid="total-earnings">{fmt(earnings?.total_earnings)}</p>
        </div>

        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#F59E0B14] flex items-center justify-center">
              <Trophy size={20} className="text-[#F59E0B]" weight="duotone" />
            </div>
            <span className="text-xs text-[#6B7280] uppercase font-medium">Placements</span>
          </div>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="placement-count">{earnings?.placement_count || 0}</p>
          {earnings?.bonus_label && (
            <span className="text-xs font-semibold text-[#F59E0B]">{earnings.bonus_label} (+{fmt(earnings.bonus_amount)})</span>
          )}
        </div>
      </div>

      {/* Commission Structure by Role */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
        <h2 className="text-base font-semibold text-[#0F172A] mb-4">Commission Structure</h2>
        <div className="space-y-4">
          {/* Show RP structure to all */}
          <div className="border border-[#E5E7EB] rounded-lg p-4">
            <h3 className="text-sm font-bold text-[#0F172A] mb-2">Recruitment Partner</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-[#6B7280]">Commission:</span> <span className="font-semibold">{rpCfg.commission_pct || 10}% of fee</span></div>
              <div><span className="text-[#6B7280]">From {rpCfg.placement_bonus_from || 4}th placement:</span> <span className="font-semibold">+{fmt(rpCfg.placement_bonus_amount || 100)}/placement</span></div>
              <div><span className="text-[#6B7280]">{rpCfg.monthly_bonus_threshold || 7}+ placements:</span> <span className="font-semibold">+{fmt(rpCfg.monthly_bonus_amount || 1000)} monthly</span></div>
              <div><span className="text-[#6B7280]">Fee starts from:</span> <span className="font-semibold">{config?.standard_fee_pct || 15}% of salary</span></div>
            </div>
          </div>

          {/* Show SRP to SRP, TL, Admin */}
          {(role === "admin" || role === "team_leader") && (
            <div className="border border-[#E5E7EB] rounded-lg p-4">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2">Senior Recruitment Partner</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-[#6B7280]">Commission:</span> <span className="font-semibold">{srpCfg.commission_pct || 13.33}% of fee</span></div>
                {(srpCfg.bonus_tiers || []).map((t, i) => (
                  <div key={i}><span className="text-[#6B7280]">{t.placements}+ placements:</span> <span className="font-semibold">{t.flat_bonus ? fmt(t.flat_bonus) + " bonus" : fmt(t.bonus_per_placement) + "/placement"}</span></div>
                ))}
              </div>
            </div>
          )}

          {/* Show TL to TL and Admin */}
          {(role === "admin" || role === "team_leader") && (
            <div className="border border-[#E5E7EB] rounded-lg p-4">
              <h3 className="text-sm font-bold text-[#0F172A] mb-2">Team Leader</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-[#6B7280]">Personal:</span> <span className="font-semibold">{tlCfg.commission_pct || 13.33}% of fee</span></div>
                <div><span className="text-[#6B7280]">Override:</span> <span className="font-semibold">{fmt(tlCfg.override_per_placement || 75)}/team placement</span></div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(tlCfg.team_bonuses || []).map((t, i) => (
                  <span key={i} className="text-xs bg-[#F1F5F9] px-2 py-1 rounded">{t.placements}+ team placements: <strong>{fmt(t.bonus)}</strong></span>
                ))}
              </div>
              {tlCfg.annual_excellence_bonus && (
                <p className="text-xs text-[#C5A059] mt-2 font-semibold">Annual Excellence: {fmt(tlCfg.annual_excellence_bonus)} (40+ placements/month for 12 consecutive months)</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Placement Details */}
      {earnings?.placements?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
          <h2 className="text-base font-semibold text-[#0F172A] mb-4">Placement Earnings</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB]">
                  <th className="text-left py-2 px-3 text-xs text-[#6B7280] font-medium">Vacancy</th>
                  <th className="text-left py-2 px-3 text-xs text-[#6B7280] font-medium">Company</th>
                  <th className="text-right py-2 px-3 text-xs text-[#6B7280] font-medium">Fee</th>
                  <th className="text-right py-2 px-3 text-xs text-[#6B7280] font-medium">Commission</th>
                  {role === "team_leader" && <th className="text-right py-2 px-3 text-xs text-[#6B7280] font-medium">Override</th>}
                  <th className="text-center py-2 px-3 text-xs text-[#6B7280] font-medium">Stage</th>
                </tr>
              </thead>
              <tbody>
                {earnings.placements.map((p, i) => (
                  <tr key={i} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`placement-row-${i}`}>
                    <td className="py-2.5 px-3 font-medium text-[#0F172A]">{p.vacancy_title}</td>
                    <td className="py-2.5 px-3 text-[#6B7280]">{p.company_name}</td>
                    <td className="py-2.5 px-3 text-right text-[#0F172A]">{fmt(p.fee)}</td>
                    <td className="py-2.5 px-3 text-right font-semibold text-[#10B981]">{fmt(p.personal_commission)}</td>
                    {role === "team_leader" && <td className="py-2.5 px-3 text-right font-semibold text-[#3B82F6]">{fmt(p.override_commission)}</td>}
                    <td className="py-2.5 px-3 text-center">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
                        {p.stage.replace(/_/g, " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calculator */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-6">
        <button onClick={() => setShowCalculator(!showCalculator)} className="flex items-center justify-between w-full" data-testid="toggle-calculator-btn">
          <h2 className="text-base font-semibold text-[#0F172A] flex items-center gap-2">
            <Calculator size={18} /> Commission Calculator
          </h2>
          {showCalculator ? <CaretUp size={18} className="text-[#6B7280]" /> : <CaretDown size={18} className="text-[#6B7280]" />}
        </button>

        {showCalculator && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Role</label>
                <select className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={roleType} onChange={e => setRoleType(e.target.value)} data-testid="calc-role">
                  <option value="recruitment_partner">Recruitment Partner</option>
                  <option value="senior_recruitment_partner">Senior Recruitment Partner</option>
                  <option value="team_leader">Team Leader</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-[#6B7280] font-medium mb-1 block">Placement Salary</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]">{"\u00a3"}</span>
                  <Input type="number" value={salary} onChange={e => setSalary(e.target.value)} className="pl-7" data-testid="calc-salary-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6B7280] font-medium mb-1 block">Fee %</label>
                  <Input type="number" value={feePercentage} onChange={e => setFeePercentage(e.target.value)} data-testid="calc-fee-input" />
                </div>
                <div>
                  <label className="text-xs text-[#6B7280] font-medium mb-1 block">No. of Placements</label>
                  <Input type="number" value={numPlacements} onChange={e => setNumPlacements(e.target.value)} data-testid="calc-placements-input" />
                </div>
              </div>
              <Button onClick={handleCalculate} className="w-full bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="calc-btn">
                <Calculator size={16} /> Calculate
              </Button>
            </div>

            {calcResult && (
              <div className="space-y-3" data-testid="calc-result">
                <div className="flex justify-between py-2 border-b border-[#F3F4F6]">
                  <span className="text-sm text-[#6B7280]">Fee per placement</span>
                  <span className="text-sm font-bold text-[#0F172A]">{fmt(calcResult.total_fee)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#F3F4F6]">
                  <span className="text-sm text-[#6B7280]">Commission per placement</span>
                  <span className="text-sm font-medium text-[#10B981]">{fmt(calcResult.commission_per_placement)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#F3F4F6]">
                  <span className="text-sm text-[#6B7280]">x {calcResult.num_placements} placements</span>
                  <span className="text-sm font-medium text-[#10B981]">{fmt(calcResult.total_commission)}</span>
                </div>
                {calcResult.bonus > 0 && (
                  <div className="flex justify-between py-2 border-b border-[#F3F4F6]">
                    <span className="text-sm text-[#6B7280]">Bonus</span>
                    <span className="text-sm font-medium text-[#F59E0B]">{fmt(calcResult.bonus)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 bg-[#0F172A] text-white -mx-4 px-4 rounded-lg">
                  <span className="text-sm font-semibold">Total Earnings</span>
                  <span className="text-lg font-bold text-[#C5A059]">{fmt(calcResult.total_earnings)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
